import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';
import {
  mapPaymentRules,
  PaymentAcceptanceRules,
  PaymentAcceptanceRulesServiceFactory,
  PaymentRulesResponseV1,
} from '@arc/unified-payments-frontend/shared-payment';
import { selectGetPaymentRulesPayload } from '../payment-rules';

/**
 * Factory that creates a reusable NgRx functional effect loading payment acceptance rules for
 * a receipt-landing page, once its own session-summary load succeeds — PaymentRuleStore gates
 * make-payment's selectInitialDataLoaded, so without this the confirmation container never
 * renders on a cold load.
 *
 * `isStripePermitted` mirrors mapPaymentRules' own flag — international allows Stripe (true),
 * POI/POA domestic don't (false). Defaults to false.
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiReceiptLandingPaymentRulesEffect = createReceiptLandingPaymentRulesEffect({
 *     loadSessionSummarySuccessAction: PoiPaymentReceiptComponentActions.loadSessionSummarySuccess,
 *     loadPaymentRulesSuccessAction: PoiPaymentReceiptComponentActions.loadPaymentRulesSuccess,
 *     loadPaymentRulesErrorAction: PoiPaymentReceiptComponentActions.loadPaymentRulesError,
 *   });
 */
export function createReceiptLandingPaymentRulesEffect<A extends Action<string>>(config: {
  loadSessionSummarySuccessAction: ActionCreator<string, Creator<any[], A>>;
  loadPaymentRulesSuccessAction: ActionCreator<
    string,
    Creator<[{ payload: PaymentRulesResponseV1 }], Action>
  >;
  loadPaymentRulesErrorAction: ActionCreator<
    string,
    Creator<[{ payload: HttpErrorResponse }], Action>
  >;
  isStripePermitted?: boolean;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      paymentAcceptanceRulesServiceFactory = inject(
        PaymentAcceptanceRulesServiceFactory,
      ),
    ) =>
      actions$.pipe(
        ofType(config.loadSessionSummarySuccessAction),
        concatLatestFrom(() => store.select(selectGetPaymentRulesPayload)),
        exhaustMap(([, getPaymentRulesPayload]) =>
          paymentAcceptanceRulesServiceFactory
            .create()
            .getPaymentAcceptanceRulesV1(
              getPaymentRulesPayload.tenantId,
              getPaymentRulesPayload.originApplication,
              getPaymentRulesPayload.cardMarsToken,
            )
            .pipe(
              map(
                (response: PaymentAcceptanceRules) =>
                  config.loadPaymentRulesSuccessAction({
                    payload: mapPaymentRules(
                      response,
                      config.isStripePermitted ?? false,
                      getPaymentRulesPayload.originApplication,
                    ),
                  }) as Action,
              ),
              catchError((error: HttpErrorResponse) =>
                of(
                  config.loadPaymentRulesErrorAction({
                    payload: error,
                  }) as Action,
                ),
              ),
            ),
        ),
      ),
    { functional: true },
  );
}
